import { defineComponent, onMounted, watch, computed, ref } from 'vue';
import { useStore } from 'vuex';
import { fabric } from 'fabric';
import { withLoading } from '@hems/component';
import { FabricHelperPro, SmartModuleHelperPro } from '@hems/util';
import { MAX_ZOOM, MIN_ZOOM, MAPPER_APP_TYPE, MAPPER_PERIOD_DATA, getMapperBackgoundColor, MAPPER_POWER_DEFAULT_VALUE, } from '@hems/util/src/constant/mapper';
export default defineComponent({
    name: 'PhysicalCanvasCore',
    props: {
        data: {
            type: String,
            default: '',
        },
        appType: {
            type: String,
            default: MAPPER_APP_TYPE.PRO,
        },
        moduleDayPwData: {
            type: Array,
            required: true,
        },
        totalPowerValue: {
            type: Object,
            default: () => MAPPER_POWER_DEFAULT_VALUE,
        },
        moduleDataListByCurrentSliderTime: {
            type: Array,
            default: null,
        },
        byDate: {
            type: String,
            default: MAPPER_PERIOD_DATA.DAY,
        },
        width: {
            type: Number,
            default: 0,
        },
        height: {
            type: Number,
            default: 0,
        },
        colorCodeArray: {
            type: Array,
            required: true,
        },
        errorModules: {
            type: Array,
            default: () => [],
        },
        moduleInfoData: {
            type: Object,
            required: true,
        },
    },
    emits: ['select'],
    setup(props, { emit }) {
        const store = useStore();
        const isDrag = ref(false);
        const activeSerial = ref('');
        const inverterSerial = ref('');
        const undoredoCanvas = new fabric.Canvas('canvas');
        const canvas = ref(undoredoCanvas);
        const moduleDataListByCurrentSliderTime = computed(() => props.moduleDataListByCurrentSliderTime);
        const [canvasWidth, canvasHeight] = SmartModuleHelperPro.getCanvasSize();
        const backgroundColor = getMapperBackgoundColor(store.state.appCtx.theme);
        // canvas init
        function init() {
            canvas.value = new fabric.Canvas('canvas', {
                selection: false,
                width: props.width || canvasWidth,
                height: props.height || canvasHeight,
                containerClass: 'inner_canvas',
                hoverCursor: 'pointer',
            });
            // Disable context menu
            document.querySelectorAll('canvas').forEach((canvas) => {
                fabric.util.addListener(canvas, 'contextmenu', (e) => {
                    e.preventDefault();
                });
            });
            initEventHandler();
            // 저장된 이력이 없는 Mapper Editor load
            if (!props.data) {
                canvas.value.setBackgroundColor(backgroundColor, canvas.value.renderAll.bind(canvas.value));
            }
            else {
                loadCanvasData();
            }
        }
        function loadCanvasData() {
            withLoading(() => {
                try {
                    canvas.value.clear();
                    canvas.value.loadFromJSON(props.data, () => {
                        FabricHelperPro.loadModuleForPhysicalLayout(canvas.value, props.appType, props.moduleDayPwData, props.colorCodeArray, props.errorModules, props.totalPowerValue);
                        FabricHelperPro.lockDragObject(canvas.value);
                        FabricHelperPro.alignmentToCenter(canvas.value);
                        canvas.value.setBackgroundColor(backgroundColor, canvas.value.renderAll.bind(canvas.value));
                    });
                }
                catch (error) {
                    console.error(error);
                }
            })();
        }
        // event
        function initEventHandler() {
            initCommonEventHandler();
            initMonitoringEventHandler();
        }
        function initCommonEventHandler() {
            canvas.value.on('mouse:wheel', (option) => {
                const event = option.e;
                handlerMouseWheel(event);
            });
            document.addEventListener('touchstart', handlerTouchStart);
        }
        function initMonitoringEventHandler() {
            canvas.value.on('mouse:down', (option) => {
                handlerMonitoringMouseDown(option);
                isDrag.value = true;
            });
            canvas.value.on('mouse:up', () => {
                isDrag.value = false;
            });
            canvas.value.on('mouse:move', (option) => {
                if (option.target === null) {
                    if ('touches' in option.e) {
                        const initPoint = {
                            x: option.pointer?.x ?? option.e.targetTouches[0].clientX,
                            y: option.pointer?.y ?? option.e.targetTouches[0].clientY,
                        };
                        if (option.e.type === 'touchmove')
                            handlerMonitoringTouchMove(option.e, initPoint);
                    }
                    else {
                        handlerMonitoringMouseMove(option.e);
                    }
                }
            });
        }
        function handlerMouseWheel(event) {
            const delta = event.deltaY;
            FabricHelperPro.setZoomInOut(canvas.value, delta, event);
        }
        let fingerDist = 0;
        function handlerTouchStart(event) {
            if (event.touches.length === 2) {
                fingerDist = SmartModuleHelperPro.getFingerDistance(event);
            }
        }
        function handlerMonitoringMouseDown(option) {
            tempLastClientX = -1;
            tempLastClientY = -1;
            if (SmartModuleHelperPro.isSelectObject(option)) {
                if (SmartModuleHelperPro.isArrayType(option.target) || SmartModuleHelperPro.isInverterType(option.target)) {
                    const selectObject = option.subTargets?.[0];
                    if (selectObject) {
                        if (SmartModuleHelperPro.isModuleType(selectObject)) {
                            // 선택된 obj 제외하고 blur 처리
                            FabricHelperPro.setActiveObject(canvas.value, selectObject);
                            // 선택된 pannel num update
                            updateActiveSerial();
                        }
                        else {
                            initActiveSerial();
                        }
                    }
                    else {
                        initActiveSerial();
                    }
                }
                else if (SmartModuleHelperPro.isTextBoxType(option.target)) {
                    initActiveSerial();
                }
            }
            else {
                initActiveSerial();
            }
        }
        function handlerMonitoringMouseMove(mouseEvent) {
            if (isDrag.value && mouseEvent && mouseEvent.clientX && mouseEvent.clientY) {
                moveEvent(mouseEvent.clientX, mouseEvent.clientY);
            }
        }
        function handlerMonitoringTouchMove(touchEvent, point) {
            if (touchEvent?.touches) {
                const singleTouchEvent = touchEvent.touches[0];
                moveEvent(singleTouchEvent.clientX, singleTouchEvent.clientY, touchEvent, point);
            }
        }
        // canvas panning을 위한 임시 값
        let tempLastClientX = -1;
        let tempLastClientY = -1;
        let pinchZoom = 1;
        function moveEvent(clientX, clientY, touchEvent, point) {
            if (touchEvent && touchEvent.touches && touchEvent.touches.length > 1) {
                const newFingerDist = SmartModuleHelperPro.getFingerDistance(touchEvent);
                pinchZoom = pinchZoom * Math.abs(newFingerDist / fingerDist);
                fingerDist = newFingerDist;
                if (pinchZoom > MAX_ZOOM) {
                    pinchZoom = MAX_ZOOM;
                }
                if (pinchZoom < MIN_ZOOM) {
                    pinchZoom = MIN_ZOOM;
                }
                canvas.value.zoomToPoint({ x: point?.x || 0, y: point?.y || 0 }, pinchZoom);
                touchEvent.preventDefault();
                touchEvent.stopPropagation();
            }
            else {
                let deltaX = 0;
                let deltaY = 0;
                if (tempLastClientX !== -1) {
                    deltaX = clientX - tempLastClientX;
                }
                if (tempLastClientY !== -1) {
                    deltaY = clientY - tempLastClientY;
                }
                tempLastClientX = clientX;
                tempLastClientY = clientY;
                canvas.value.relativePan(new fabric.Point(deltaX, deltaY));
            }
        }
        function updateActiveSerial() {
            const activeObject = canvas.value.getActiveObject();
            if (activeObject) {
                if (SmartModuleHelperPro.isModuleType(activeObject)) {
                    inverterSerial.value = SmartModuleHelperPro.getSerialNumber(activeObject); // physical의 경우 qserial이 mi s/n값.
                    activeSerial.value =
                        props.moduleInfoData?.deviceIdList?.find((info) => info.deviceId === inverterSerial.value)?.moduleId ?? '';
                    emit('select', activeSerial.value, inverterSerial.value);
                }
            }
        }
        function initActiveSerial() {
            activeSerial.value = '';
            inverterSerial.value = '';
            FabricHelperPro.unSelectActiveObject(canvas.value);
            emit('select', activeSerial.value, inverterSerial.value);
        }
        const processButtonEvent = (event) => {
            FabricHelperPro.mobileButtonEvent(canvas.value, event);
        };
        onMounted(() => {
            init();
        });
        watch(() => [props.moduleDayPwData, props.totalPowerValue], () => {
            loadCanvasData();
        });
        watch(() => [moduleDataListByCurrentSliderTime.value, props.totalPowerValue], () => {
            FabricHelperPro.setPowerText(canvas.value, moduleDataListByCurrentSliderTime.value, props.errorModules, props.byDate, props.colorCodeArray, props.totalPowerValue);
        });
        return {
            initActiveSerial,
            processButtonEvent,
        };
    },
});
